const SfxAuthenticationCodeDialog = require('@feature/login/components/authentication-code-dialog/authentication-code-dialog.component').SfxAuthenticationCodeDialog;
const SfxEnterRecoveryCodeDialog = require('@feature/login/components/enter-recovery-code-dialog/enter-recovery-code-dialog.component').SfxEnterRecoveryCodeDialog;

(function () {
    'use strict';

    const OAUTH_INTERACTION_PATH_PREFIX = '/oidc/interaction/';

    angular
        .module('salesflare')
        .controller('LogInController', logInController);

    function logInController(matDialogService, zoneService, $mdDialog, $scope, $state, $window, $exceptionHandler, $translate, helperFunctionsService, model, authentication, utils, config, datasources, $location) {

        $scope.oniOS = ($window.device && $window.device.platform === 'iOS');
        $scope.disableButtons = false;
        $scope.copyRightYear = new Date().getFullYear();
        $scope.inOutlook = config.mode === 'outlook-web';

        $scope.handleRecaptchaDisclaimerClick = function ($event) {
            if ($event.target.tagName === 'A') {
                $event.preventDefault();
                $window.open($event.target.getAttribute('href'), '_blank', 'noopener');
            }
        };

        const connectInfo = {
            google: {}
        };

        init();

        $scope.logIn = function logIn(username, password) {

            $scope.disableButtons = true;

            $scope.loginForm.password.$warning = {};
            $scope.loginForm.password.$setValidity('general', true);
            $scope.loginForm.email.$setValidity('general', true);
            $scope.emailErrorText = undefined;
            $scope.passwordErrorText = undefined;
            $scope.passwordWarningText = undefined;

            if (!!username && !!password) {

                // Mobile doesn't get loaded over http, so it can't be accepted in the google reCAPTCHA settings
                if ($window.isMobile) {
                    return authenticate(username, password, null);
                }

                return $window.grecaptcha.enterprise.ready(function () {

                    return $window.grecaptcha.enterprise.execute('6LfJFycsAAAAAEyuhHf2S9pRZPEIoQbhZrLUs4Q0', { action: 'LOGIN' }).then(function (captchaToken) {

                        return authenticate(username, password, captchaToken);
                    }, function (err) {

                        return $scope.$applyAsync(function () {

                            $scope.disableButtons = false;

                            if (err) {
                                $exceptionHandler(err);
                            }
                        });
                    });
                }, function (error) {

                    return $scope.$applyAsync(function () {

                        $scope.disableButtons = false;

                        if (error) {
                            $exceptionHandler(error);
                        }
                    });
                });
            }

            $scope.disableButtons = false;
        };

        function normalizeServerMessage(msg) {
            if (!msg) {
                return msg;
            }

            const map = {
                'User not found': 'User not found',
                'There is no Salesflare user associated with this email':
                    'There is no Salesflare user associated with this email'
            };
            if (map[msg]) {
                return map[msg];
            }

            if (msg.includes('You have no Salesflare password set')) {
                return 'You have no Salesflare password set';
            }

            return msg;
        }

        function authenticate(username, password, captchaToken) {

            if ($window.isMobile) {
                captchaToken = null;
            }

            return authentication.logIn(username, password, captchaToken)
                .then(function (response) {

                    return setTokenAndRedirectAfterLogin(response.data.token);
                })
                .catch(function (err) {

                    $scope.disableButtons = false;

                    if (!err.data) {
                        return;
                    }

                    const errorMsg = normalizeServerMessage(err.data.message);

                    if (errorMsg === 'User not found' || errorMsg === 'There is no Salesflare user associated with this email') {
                        $scope.loginForm.email.$setValidity('general', false);
                        $scope.emailErrorText = $translate.instant(errorMsg);
                    }
                    else if (errorMsg.includes('You have no Salesflare password set')) {
                        $scope.loginForm.password.$warning.general = true;
                        $scope.passwordWarningText = $translate.instant(errorMsg);
                    }
                    else if (errorMsg.toLowerCase().includes('two-factor authentication is required')) {

                        zoneService.run(() => {
                            const authenticationDialogRef = matDialogService.open(SfxAuthenticationCodeDialog, {
                                panelClass: ['sfx-dialog-container', 'sfx-authentication-code-dialog-container'],
                                data: {
                                    email: username,
                                    password
                                }
                            });

                            authenticationDialogRef.afterClosed().subscribe((authenticationDialogResult) => {

                                if (authenticationDialogResult?.token) {
                                    return setTokenAndRedirectAfterLogin(authenticationDialogResult.token);
                                }

                                if (authenticationDialogResult?.action === 'SHOW_RECOVERY_CODE_DIALOG') {
                                    const recoveryCodeDialogRef = matDialogService.open(SfxEnterRecoveryCodeDialog, {
                                        panelClass: ['sfx-dialog-container', 'sfx-enter-recovery-code-dialog-container'],
                                        data: {
                                            email: username,
                                            password
                                        }
                                    });

                                    recoveryCodeDialogRef.afterClosed().subscribe((recoveryDialogResult) => {

                                        if (recoveryDialogResult?.token) {
                                            return setTokenAndRedirectAfterLogin(recoveryDialogResult.token);
                                        }
                                    });
                                }
                            });
                        });
                    }
                    else if (err.data.statusCode === 429) {
                        $scope.loginForm.password.$setValidity('general', false);
                        $scope.passwordErrorText = $translate.instant('You have exceeded the login attempt limit, try again in a minute.');
                    }
                    else {
                        $scope.loginForm.password.$setValidity('general', false);
                        $scope.passwordErrorText = errorMsg;
                    }
                });
        }

        $scope.logInGoogle = function () {

            $scope.disableButtons = true;

            if (!$window.isMobile) {
                return showAuthenticationPopUp(`${config.apiUrl}datasources/connect?type=google&is_sign_in=true`).then(connectCallback);
            }

            return $window.plugins.googleplus.login(connectInfo.google, function (googleAuthData) {

                googleAuthData.is_sign_in = true;

                const tradePromise = datasources.trade(googleAuthData, { type: 'google' }, { noToast: true, noToastForStatusCode: [403] });

                tradePromise
                    .then(function (response) {

                        return connectCallback(response.data);
                    })
                    .catch(function (err) {

                        if (err?.data?.message === 'UNAUTHORIZED_SCOPES') {
                            $scope.disableButtons = false;

                            const insufficientPermissionsDialog = $mdDialog.confirm({ multiple: true })
                                .clickOutsideToClose(true)
                                .escapeToClose(true)
                                .title($translate.instant('Please enable all permissions'))
                                .textContent($translate.instant('Salesflare needs all the requested permissions to be able to automate your CRM work.'))
                                .ok($translate.instant('Enable'))
                                .cancel($translate.instant('cancel'));

                            return $mdDialog.show(insufficientPermissionsDialog)
                                .then(() => {
                                    $scope.connectGoogle();
                                });
                        }

                        $exceptionHandler(err);
                        $scope.disableButtons = false;
                    });

                tradePromise.catch(function (err) {

                    $exceptionHandler(err);
                    $scope.disableButtons = false;

                    return $window.plugins.googleplus.logout(function (msg) {

                        if (msg !== 'Logged user out' && msg !== 'logged out') {
                            $exceptionHandler(msg);
                            return utils.showErrorToast($translate.instant('Oops! Something went wrong. Please try again.'));
                        }
                    });
                });
            }, function (msg) {

                // Apply since not in angular digest loop here and we do want to update the view
                $scope.$apply(function () {

                    $scope.disableButtons = false;
                });

                // 12501 is user canceled the oauth flow
                if (msg && msg !== 12501) {
                    $exceptionHandler(msg);
                    utils.showErrorToast($translate.instant('Oops! Something went wrong. Please try again.'));
                }

                $window.plugins.googleplus.logout(function (message) {

                    if (message !== 'Logged user out') {
                        $exceptionHandler(message);
                        return utils.showErrorToast($translate.instant('Oops! Something went wrong. Please try again.'));
                    }
                });
            });
        };

        $scope.logInMicrosoft = function () {

            $scope.disableButtons = true;

            return showAuthenticationPopUp(`${config.apiUrl}datasources/connect?type=office365&is_sign_in=true`).then(connectCallback).catch(function () {

                $scope.disableButtons = false;
            });
        };

        function showAuthenticationPopUp(url) {

            if (config.mode === 'outlook-web') {
                return helperFunctionsService.handleOutlookPluginOAuthFlow(url);
            }

            return utils.popup(url);
        }

        /**
         * Validates the `returnTo` query parameter so we only ever redirect to our own OAuth provider interaction endpoint.
         * Anything else, including other origins and non http(s) schemes such as `javascript:`, is rejected.
         *
         * @param {String} [rawReturnTo] The raw `returnTo` query parameter.
         * @returns {String|null} The validated absolute URL, or `null` when it is missing or not allowed.
         */
        function getSafeReturnTo(rawReturnTo) {

            if (!rawReturnTo) {
                return null;
            }

            let returnToUrl;

            try {
                // `$location.search()` already decodes once, the OAuth provider encodes the value before putting it in the hash
                returnToUrl = new URL(decodeURIComponent(rawReturnTo));
            }
            catch {
                return null;
            }

            if (returnToUrl.origin !== new URL(config.apiUrl).origin) {
                return null;
            }

            if (!returnToUrl.pathname.startsWith(OAUTH_INTERACTION_PATH_PREFIX)) {
                return null;
            }

            return returnToUrl.href;
        }

        function init() {

            const rawReturnTo = $location.search().returnTo;

            $scope.returnTo = getSafeReturnTo(rawReturnTo);

            if (rawReturnTo && !$scope.returnTo) {
                $exceptionHandler(new Error('Ignored a returnTo query parameter on the login page that is not an allowed OAuth interaction URL'));
            }

            if (model.isLoggedIn && !$scope.returnTo) {
                return $state.go('tasks.today');
            }

            if ($window.isMobile) {
                datasources.getConnectInfo('google').then(function (response) {

                    connectInfo.google = response.data;
                });
            }
        }

        function connectCallback(queryParams) {

            if (!queryParams) {
                $scope.disableButtons = false;

                return $exceptionHandler(new Error('Lost connection to the popup window or it was manually closed'));
            }

            if (queryParams.get('success') !== 'true') {
                $scope.disableButtons = false;

                return utils.showErrorToast(queryParams.get('error'));
            }

            return setTokenAndRedirectAfterLogin(queryParams.get('token'));
        }

        function setTokenAndRedirectAfterLogin(token) {

            // If it is oauth flow login we should redirect to the returnTo query param

            model.setToken(token);

            // Return $state.go('tasks.today');
            if ($scope.returnTo) {
                $window.location.href = $scope.returnTo;
            }
            else {
                $state.go('tasks.today');
            }
        }
    }
})();
